import { useEffect, useMemo, useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import type {
  ApiClient, Elektrotyp, Elektrofeld, Elektrobauteilart,
} from '../api';
import { IconWarnung } from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, Feld as MusterFeld,
  FormSection, KpiCard, KpiReihe, LoadingState, StatusBadge, leseZahl,
  type Spalte,
} from '../muster';
import { symbolFuer } from '../ElektroSymbole';
import { inDieAblage } from '../kopieren';

interface Rahmen { api: ApiClient; projekt?: { id: string } }

/**
 * Die Datenpflege je Fabrikat.
 *
 * --- Warum je Fabrikat und nicht je Bauteil -------------------------------------
 *
 * 28.597 Bauteile, aber nur 87 Fabrikate. Ein Lichtstrom gehört zum Fabrikat, nicht
 * zum Exemplar: Wer ihn an einer einzelnen Leuchte pflegt, ändert das Produkt — und
 * die übrigen 5.499 bleiben, wie sie waren.
 *
 * Die Seite pflegt deshalb Typwerte. Was je Bauteil gilt — Raumnummer, Verteiler,
 * DALI-Adresse — steht in der Baumansicht.
 *
 * --- Drei Wege, dieselben Werte zu füllen ----------------------------------------
 *
 *   1  Von Hand, Feld für Feld
 *   2  Als JSON-Datei, die ein Hersteller aus seiner Tabelle erzeugt
 *   3  Aus einem PDF-Datenblatt — das liest die Datenblattauswertung
 *
 * Von Hand gepflegte Werte werden von keinem der anderen Wege überschrieben. Wer sie
 * eingetragen hat, hatte das Datenblatt vor sich; eine Herstellerdatei ist allgemein.
 */
export function ElektroDatenpflege() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [typen, setTypen] = useState<Elektrotyp[] | null>(null);
  const [felder, setFelder] = useState<Elektrofeld[]>([]);
  const [arten, setArten] = useState<Elektrobauteilart[]>([]);
  const [art, setArt] = useState('');
  const [nurOffen, setNurOffen] = useState(false);
  const [suche, setSuche] = useState('');
  const [offen, setOffen] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = async () => {
    if (!projekt) return;

    try {
      const [t, f, a] = await Promise.all([
        api.elektroTypen(projekt.id, {
          bauteilart: art || undefined,
          nurOhneDatenblatt: nurOffen || undefined,
        }),
        api.elektroFelder(),
        api.elektroBauteilarten(),
      ]);

      setTypen(t);
      setFelder(f.filter((x) => x.giltJeTyp));
      setArten(a.filter((x) => x.wirdGefuehrt));
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => { laden(); }, [projekt?.id, art, nurOffen]);

  const gefiltert = useMemo(() => {
    if (!typen) return [];
    if (!suche.trim()) return typen;

    const s = suche.toLowerCase();

    return typen.filter((t) =>
      (t.familie ?? '').toLowerCase().includes(s)
      || (t.typ ?? '').toLowerCase().includes(s)
      || t.bezeichnung.toLowerCase().includes(s));
  }, [typen, suche]);

  const kopf = (
    <PageHeader
      titel="Elektro — Datenpflege je Fabrikat"
      beschreibung={'28.597 Bauteile, aber nur 87 Fabrikate. Ein Lichtstrom gehört '
        + 'zum Fabrikat und nicht zum Exemplar — diese Seite pflegt deshalb '
        + 'Typwerte. Was je Bauteil gilt (Raumnummer, Verteiler, DALI-Adresse), '
        + 'steht in der Baumansicht.'}
      meta={typen ? (
        <>
          {typen.length} Fabrikate ·{' '}
          {typen.reduce((n, t) => n + t.anzahl, 0).toLocaleString('de-DE')} Bauteile
        </>
      ) : undefined}
      aktionen={(
        <Link to="../elektro" className="m-knopf" style={{ textDecoration: 'none' }}>zur Baumansicht</Link>
      )}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Die Datenpflege konnte nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={() => { setFehler(null); laden(); }}
        />
      </>
    );
  }

  if (!projekt || !typen) {
    return (
      <>
        {kopf}
        <LoadingState text="Fabrikate werden geladen …" zeilen={10} spalten={5} />
      </>
    );
  }

  const gesamt = typen.reduce((n, t) => n + t.anzahl, 0);
  const mitBlatt = typen.filter((t) => t.mitDatenblatt > 0).length;
  const gewaehlt = gefiltert.find((t) => `${t.familie}|${t.typ}` === offen) ?? null;

  const spalten: Spalte<Elektrotyp>[] = [
    {
      schluessel: 'fabrikat', titel: 'Fabrikat', immerSichtbar: true,
      wert: (t) => `${t.typ ?? ''} ${t.familie ?? ''}`.trim(),
      inhalt: (t) => {
        const Sym = symbolFuer(t.bauteilart);
        return (
          <>
            <Sym size={15} title={t.bezeichnung} />
            <span style={{ marginLeft: 6 }}>{t.typ ?? '—'}</span>
            <div className="sehr-schwach" style={{ fontSize: 10, marginLeft: 21 }}>
              {t.familie}
            </div>
          </>
        );
      },
    },
    { schluessel: 'anzahl', titel: 'Stück', art: 'zahl', wert: (t) => t.anzahl },
    {
      schluessel: 'werte', titel: 'Werte', ausrichtung: 'rechts',
      wert: (t) => t.werte.filter((w) => w.wert).length,
      inhalt: (t) => {
        const eigene = felder.filter((f) =>
          !f.bauteilarten
          || f.bauteilarten.split('|').some((a) =>
              a.trim().toLowerCase() === t.bauteilart.toLowerCase()));
        const g = t.werte.filter((w) => w.wert).length;

        return (
          <span className="schwach" style={{ fontSize: 11 }}>
            {g} von {eigene.length}
            {g === 0 && eigene.length > 0 && (
              <StatusBadge zustand="neutral" text="nichts gepflegt" klein />
            )}
          </span>
        );
      },
    },
    {
      schluessel: 'datenblatt', titel: 'Datenblatt', art: 'status', breite: '170px',
      wert: (t) => (t.verweis ? 'vorhanden' : t.brauchtDatenblatt ? 'fehlt' : '—'),
      inhalt: (t) => (t.verweis
        ? <StatusBadge zustand="gut" text="vorhanden" klein titel="ohne Anmeldung erreichbar" />
        : t.brauchtDatenblatt
          ? (
            <StatusBadge
              zustand="warnung" text="fehlt" klein
              titel="Für diese Bauteilart ist ein Datenblatt vorgesehen"
            />
          )
          : <span className="sehr-schwach">—</span>),
    },
  ];

  return (
    <>
      {kopf}

      {meldung && (
        <div className="hinweis" style={{ display: 'block', marginBottom: 12 }}>
          {meldung}
          <button className="m-knopf" onClick={() => setMeldung(null)}
                  style={{ marginLeft: 10, padding: '1px 7px', fontSize: 11 }}>
            schließen
          </button>
        </div>
      )}

      <KpiReihe>
        <KpiCard titel="Fabrikate" wert={typen.length} />
        <KpiCard titel="Bauteile" wert={gesamt} />
        <KpiCard
          titel="mit Datenblatt" wert={mitBlatt}
          vergleich={`von ${typen.length} Fabrikaten`}
          zustand={typen.length === 0 ? undefined
            : mitBlatt < typen.length ? 'warnung' : 'gut'}
          zustandText={typen.length === 0 ? undefined
            : mitBlatt < typen.length
              ? `${typen.length - mitBlatt} ohne Datenblatt` : 'vollständig'}
        />
      </KpiReihe>

      {/* Im Kopf bleibt nur die Vorlage. Das Einlesen geschieht am gewählten
          Fabrikat: Dort ist es schon gewählt, und die Datei braucht keine
          Familie und keinen Typ — was auf dem Bildschirm markiert ist, muss
          man nicht abschreiben. */}
      <ContentSection titel="Vorlage" nackt>
        <Vorlagenknopf api={api} projektId={projekt.id} art={art}
                       melde={setMeldung} neuLaden={laden} />
      </ContentSection>

      <DataTable
        name="Fabrikate"
        spalten={spalten}
        zeilen={gefiltert}
        schluessel={(t) => `${t.familie}|${t.typ}`}
        suchbegriff={suche}
        beiSuche={setSuche}
        suchePlatzhalter="Familie oder Typ"
        ausfuhr
        ausfuhrDateiname="elektro-fabrikate"
        zeilenhoehe={48}
        beiZeileKlick={(t) => setOffen(
          offen === `${t.familie}|${t.typ}` ? null : `${t.familie}|${t.typ}`)}
        aktiveZeile={offen ?? undefined}
        werkzeuge={(
          <>
            <select value={art} onChange={(e) => setArt(e.target.value)}
                    aria-label="Bauteilart" style={{ width: 'auto' }}>
              <option value="">alle Bauteilarten</option>
              {arten.map((a) => (
                <option key={a.schluessel} value={a.schluessel}>
                  {a.bezeichnung}
                </option>
              ))}
            </select>

            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
              <input type="checkbox" checked={nurOffen}
                     onChange={(e) => setNurOffen(e.target.checked)} />
              nur ohne Datenblatt
            </label>
          </>
        )}
        leer={(
          <EmptyState
            titel={art || nurOffen || suche
              ? 'Kein Fabrikat trifft diese Auswahl.'
              : 'Für dieses Modell ist noch kein Elektrofabrikat aufgenommen.'}
            erklaerung={art || nurOffen || suche
              ? 'Bauteilart, Datenblattstand und Suche schränken die Liste ein.'
              : 'Die Fabrikate entstehen aus der Modellaufnahme des Gewerks Elektro.'}
            aktion={(art || nurOffen || suche) ? (
              <button className="m-knopf"
                      onClick={() => { setArt(''); setNurOffen(false); setSuche(''); }}>
                Filter aufheben
              </button>
            ) : undefined}
          />
        )}
      />

      {gewaehlt && (
        <Typdetail
          key={`${gewaehlt.familie}|${gewaehlt.typ}`}
          t={gewaehlt}
          api={api}
          projektId={projekt.id}
          felder={felder}
          schliessen={() => setOffen(null)}
          fertig={(m) => { setMeldung(m); laden(); }}
        />
      )}

      <ContentSection titel="Herkunft der Werte" nackt>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 11 }}>
          <StatusBadge zustand="gut" text="aus dem Modell gelesen" klein />
          <StatusBadge zustand="info" text="nur in der Datenbank" klein />
          <span className="schwach">
            Von Hand gepflegte Werte werden von Import und Datenblattauswertung
            nicht überschrieben.
          </span>
        </div>
      </ContentSection>
    </>
  );
}

// === Das Detailfeld eines Fabrikats ==========================================

function Typdetail({
  t, api, projektId, felder, schliessen, fertig,
}: {
  t: Elektrotyp;
  api: ApiClient;
  projektId: string;
  felder: Elektrofeld[];
  schliessen: () => void;
  fertig: (m: string) => void;
}) {
  const [laedt, setLaedt] = useState(false);

  // Die Rückmeldung zum Kopieren. 'kopiert' verschwindet nach zwei Sekunden,
  // ein Fehlschlag bleibt stehen und zeigt den Verweis zum Markieren.
  const [kopiert, setKopiert] = useState<'ja' | 'nein' | null>(null);

  // Der JSON-Import je Fabrikat. Die geprüfte Datei bleibt liegen, bis
  // übernommen oder verworfen wird — sonst müsste man sie zweimal wählen.
  const [entwurf, setEntwurf] = useState<File | null>(null);
  const [pruefung, setPruefung] = useState<string[] | null>(null);

  // Nur die Felder, die zu dieser Bauteilart gehören. Ein Lichtstrom an einem
  // Rauchmelder wäre eine Einladung zum Fehler.
  const eigene = useMemo(
    () => felder.filter((f) =>
      !f.bauteilarten
      || f.bauteilarten.split('|').some((a) =>
          a.trim().toLowerCase() === t.bauteilart.toLowerCase())),
    [felder, t.bauteilart],
  );

  /**
   * Die JSON-Datei prüfen, ohne zu ändern.
   *
   * Die Prüfansicht bleibt stehen, bis übernommen oder verworfen wird — sie ist
   * die Grundlage der Entscheidung, und eine Meldung, die man wegklickt, wäre es
   * nicht.
   */
  const pruefen = async (datei: File) => {
    setLaedt(true);
    setPruefung(null);

    try {
      const a = await api.elektroDatenImport(
        projektId, datei, true, t.familie, t.typ);

      if (!a) { fertig('Keine Antwort von der Schnittstelle.'); return; }

      const zeilen = [a.message];

      if (a.unbekannteFelder.length > 0)
        zeilen.push('Unbekannte Felder: ' + a.unbekannteFelder.join(', '));

      if (a.abgewiesen.length > 0)
        zeilen.push('Abgewiesen: ' + a.abgewiesen.join('; '));

      if (a.unbekannteFabrikate.length > 0)
        zeilen.push('Nicht im Projekt: ' + a.unbekannteFabrikate.join(', '));

      if (a.uebergangen.length > 0)
        zeilen.push('NICHT eingelesen, weil eine andere Zeile gewählt ist: '
          + a.uebergangen.join(', ')
          + '. Für sie die jeweilige Zeile wählen oder oben „alle Fabrikate '
          + 'einlesen".');

      setEntwurf(datei);
      setPruefung(zeilen);
    } catch (e) {
      fertig(e instanceof Error ? e.message : String(e));
    } finally {
      setLaedt(false);
    }
  };

  /** Die geprüfte Datei übernehmen. */
  const uebernehmen = async () => {
    if (!entwurf) return;

    setLaedt(true);
    try {
      const a = await api.elektroDatenImport(
        projektId, entwurf, false, t.familie, t.typ);

      setEntwurf(null);
      setPruefung(null);

      fertig(a ? a.message : 'Übernommen.');
    } catch (e) {
      fertig(e instanceof Error ? e.message : String(e));
    } finally {
      setLaedt(false);
    }
  };

  const hochladen = async (datei: File) => {
    setLaedt(true);
    try {
      const a = await api.elektroDatenblattHochladen(
        projektId, datei, t.familie, t.typ);

      fertig(a.message);
    } catch (e) {
      fertig(e instanceof Error ? e.message : String(e));
    } finally {
      setLaedt(false);
    }
  };

  return (
    <ContentSection
      titel={t.typ ?? t.familie ?? 'Fabrikat'}
      beschreibung={t.familie}
      meta={`${t.anzahl.toLocaleString('de-DE')} Bauteil(e)`}
      aktionen={(
        <>
          {/* --- Die beiden Wege, am gewählten Fabrikat -------------------
              Das Fabrikat ist hier schon gewählt. Wer eine Datei ohne diese
              Auswahl hochlädt, muss Familie und Typ hineinschreiben — und
              ein Tippfehler darin findet das Fabrikat nicht wieder.
              (Vorher standen diese beiden Knöpfe in der Tabellenzeile; sie
              sind mit dem Umzug auf `DataTable` hierher gewandert, weil ein
              Klick auf die Zeile ohnehin genau dieses Fabrikat wählt.) */}
          <label className="m-knopf" title="PDF-Datenblatt für dieses Fabrikat">
            {laedt ? '…' : 'PDF'}
            <input type="file" accept="application/pdf,.pdf" hidden
                   disabled={laedt}
                   onChange={(e) => {
                     const d = e.target.files?.[0];
                     if (d) hochladen(d);
                     e.target.value = '';
                   }} />
          </label>

          <label className="m-knopf"
                 title="Werte als JSON für dieses Fabrikat — erst prüfen">
            JSON
            <input type="file" accept="application/json,.json" hidden
                   disabled={laedt}
                   onChange={(e) => {
                     const d = e.target.files?.[0];
                     if (d) pruefen(d);
                     e.target.value = '';
                   }} />
          </label>

          <button className="m-knopf m-knopf-still" onClick={schliessen}>
            Schließen
          </button>
        </>
      )}
    >
            {/* --- Das Datenblatt ------------------------------------------- */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center',
                          flexWrap: 'wrap', marginBottom: 14, fontSize: 12 }}>
              {t.verweis ? (
                <>
                  <a href={t.verweis} target="_blank" rel="noreferrer">
                    Datenblatt öffnen
                  </a>

                  <button
                    onClick={async () => {
                      const geklappt = await inDieAblage(t.verweis ?? '');

                      setKopiert(geklappt ? 'ja' : 'nein');

                      // Der Erfolg verschwindet wieder, der Fehlschlag bleibt:
                      // Er verlangt eine Handlung.
                      if (geklappt) setTimeout(() => setKopiert(null), 2000);
                    }}
                    style={{ padding: '2px 8px', fontSize: 11 }}
                    title="Der Verweis ist ohne Anmeldung erreichbar"
                  >
                    {kopiert === 'ja' ? 'kopiert ✓' : 'Verweis kopieren'}
                  </button>

                  <label style={{ fontSize: 11, cursor: 'pointer',
                                  textDecoration: 'underline' }}>
                    ersetzen
                    <input type="file" accept="application/pdf,.pdf" hidden
                           disabled={laedt}
                           onChange={(e) => {
                             const d = e.target.files?.[0];
                             if (d) hochladen(d);
                             e.target.value = '';
                           }} />
                  </label>

                  <span className="sehr-schwach" style={{ fontSize: 10 }}>
                    Ein Ersetzen behält denselben Verweis — bereits ins Modell
                    geschriebene Verweise bleiben gültig.
                  </span>

                  {/* Wenn der Browser das Kopieren verweigert: den Verweis zum
                      Markieren. Selbst kopieren ist besser als ein Knopf, der
                      nichts tut. */}
                  {kopiert === 'nein' && (
                    <div style={{ flexBasis: '100%', marginTop: 4 }}>
                      <div className="sehr-schwach" style={{ fontSize: 10 }}>
                        Der Browser erlaubt das Kopieren nicht — hier markieren:
                      </div>

                      <input
                        readOnly
                        value={t.verweis ?? ''}
                        onFocus={(e) => e.currentTarget.select()}
                        style={{ width: '100%', fontSize: 11,
                                 fontFamily: 'monospace' }}
                      />
                    </div>
                  )}
                </>
              ) : (
                <>
                  <label className="knopf" style={{ padding: '3px 8px',
                                                    fontSize: 11 }}>
                    {laedt ? 'wird abgelegt …' : 'PDF-Datenblatt hochladen'}
                    <input type="file" accept="application/pdf,.pdf" hidden
                           disabled={laedt}
                           onChange={(e) => {
                             const d = e.target.files?.[0];
                             if (d) hochladen(d);
                             e.target.value = '';
                           }} />
                  </label>

                  <span className="sehr-schwach" style={{ fontSize: 10 }}>
                    Der Verweis wird allen {t.anzahl} Bauteil(en) dieses Fabrikats
                    zugewiesen und geht beim Zurückschreiben ins Modell.
                  </span>
                </>
              )}

              {t.herstellerUrl && (
                <a href={t.herstellerUrl} target="_blank" rel="noreferrer"
                   style={{ fontSize: 11 }}>
                  Herstellerseite
                </a>
              )}
            </div>

            {/* --- Die Prüfansicht des JSON-Imports ------------------------- */}
            {pruefung && (
              <div className="hinweis" style={{ display: 'block', marginBottom: 14 }}>
                <IconWarnung size={13} />

                <div style={{ fontSize: 12, whiteSpace: 'pre-line', flex: 1 }}>
                  {pruefung.join('\n')}
                </div>

                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button onClick={uebernehmen} disabled={laedt}
                          className="betont" style={{ fontSize: 11 }}>
                    {laedt ? 'wird eingelesen …' : 'übernehmen'}
                  </button>

                  <button onClick={() => { setEntwurf(null); setPruefung(null); }}
                          style={{ fontSize: 11 }}>
                    verwerfen
                  </button>

                  <span className="sehr-schwach"
                        style={{ fontSize: 10, alignSelf: 'center' }}>
                    Von Hand gepflegte Werte bleiben in beiden Fällen unberührt.
                  </span>
                </div>
              </div>
            )}

            {/* --- Die Werte ------------------------------------------------ */}
            <Werteformular
              api={api}
              projektId={projektId}
              t={t}
              felder={eigene}
              fertig={fertig}
            />
    </ContentSection>
  );
}

// === Das Werteformular ======================================================

function Werteformular({
  api, projektId, t, felder, fertig,
}: {
  api: ApiClient;
  projektId: string;
  t: Elektrotyp;
  felder: Elektrofeld[];
  fertig: (m: string) => void;
}) {
  const [entwurf, setEntwurf] = useState<Record<string, string>>(() =>
    Object.fromEntries(t.werte.map((w) => [w.feldschluessel, w.wert ?? ''])));

  const [laeuft, setLaeuft] = useState(false);

  const herkunft = useMemo(
    () => Object.fromEntries(t.werte.map((w) => [w.feldschluessel, w.herkunft])),
    [t.werte],
  );

  // Nach Gruppe ordnen, wie in der Baumansicht. Fünfzig Felder in einer Reihe
  // sind unlesbar.
  const gruppen = useMemo(() => {
    const aus: { name: string; felder: Elektrofeld[] }[] = [];

    for (const f of felder) {
      let g = aus.find((x) => x.name === f.gruppe);

      if (!g) { g = { name: f.gruppe, felder: [] }; aus.push(g); }

      g.felder.push(f);
    }

    return aus;
  }, [felder]);

  const geaendert = felder.some((f) =>
    (entwurf[f.feldschluessel] ?? '')
      !== (t.werte.find((w) => w.feldschluessel === f.feldschluessel)?.wert ?? ''));

  const speichern = async () => {
    setLaeuft(true);
    try {
      // Nur das Geänderte schicken. Alles zu senden würde auch die
      // Modellwerte als handgepflegt eintragen.
      const werte: Record<string, string | null> = {};

      for (const f of felder) {
        const neu = entwurf[f.feldschluessel] ?? '';
        const alt = t.werte.find((w) =>
          w.feldschluessel === f.feldschluessel)?.wert ?? '';

        if (neu !== alt) werte[f.feldschluessel] = neu === '' ? null : neu;
      }

      const a = await api.elektroTypwerteSpeichern(
        projektId, t.familie ?? '', t.typ ?? '', werte);

      fertig(a ? a.message : 'Gespeichert.');
    } catch (e) {
      fertig(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  if (felder.length === 0) {
    return (
      <EmptyState
        klein
        titel="Für diese Bauteilart sind keine Fabrikatswerte vorgesehen."
        erklaerung="Welche Felder gelten, entscheidet der Feldkatalog je Bauteilart."
      />
    );
  }

  return (
    <>
      {gruppen.map((g, i) => (
        <FormSection
          key={g.name}
          titel={g.name}
          spalten={3}
          fuss={i === gruppen.length - 1 ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button onClick={speichern} disabled={!geaendert || laeuft}
                      className="m-knopf m-knopf-haupt">
                {laeuft ? 'wird gespeichert …' : 'Speichern'}
              </button>

              <span className="sehr-schwach" style={{ fontSize: 11 }}>
                gilt für alle {t.anzahl} Bauteil(e) dieses Fabrikats
              </span>
            </div>
          ) : undefined}
        >
          {g.felder.map((f) => (
            <Feld
              key={f.feldschluessel}
              f={f}
              wert={entwurf[f.feldschluessel] ?? ''}
              herkunft={herkunft[f.feldschluessel]}
              setzen={(w) => setEntwurf({ ...entwurf, [f.feldschluessel]: w })}
            />
          ))}
        </FormSection>
      ))}
    </>
  );
}

/**
 * Ein Fabrikatswert.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WERT UND EINHEIT GETRENNT (Abschnitt 15, Paket U6b)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Vorher stand die Einheit in der Beschriftung (`Lichtstrom [lm]`). Ein
 * Zahlenfeld reicht jetzt an `WertEinheitFeld` durch — die Einheit steht in
 * einem eigenen Element neben dem Feld.
 *
 * Der Zustand bleibt eine Zeichenkette, weil die Änderungserkennung und der
 * Aufbau der Nutzlast darauf stehen: Ein leeres Feld ist ein Löschbefehl und
 * darf nicht zu 0 werden.
 *
 * Die Herkunft stand als Sonderzeichen (✓ ≡ ▣) neben der Beschriftung — ein
 * Zeichen ohne Text. Sie steht jetzt als Hinweis unter dem Feld.
 */
function Feld({
  f, wert, herkunft, setzen,
}: {
  f: Elektrofeld;
  wert: string;
  herkunft?: string;
  setzen: (w: string) => void;
}) {
  const auswahl = f.auswahl?.split('|').filter(Boolean) ?? [];

  const herkunftstext = herkunft === 'hand' ? 'von Hand gepflegt'
    : herkunft === 'import' ? 'aus einem Import'
      : herkunft === 'datenblatt' ? 'aus dem Datenblatt'
        : undefined;

  /* Zwei Auskünfte, die nicht dasselbe sagen (PB53):
     `ausModellGelesen` sagt, WOHER der Wert kommt — der Parameter ist ein
     fremder und keiner aus unserer eigenen Familienpflege („TuV_…").
     `gehtInsModell` sagt, WOHIN er geht. Bis PB53 stand hier nur das erste,
     und es hieß `ausModell`: 28 Felder trugen den Satz, 102 gehen zurück. */
  const hinweis = [
    f.hinweis ?? undefined,
    herkunftstext,
    f.ausModellGelesen ? 'aus dem Modell gelesen' : 'nur in der Datenbank',
    f.gehtInsModell
      ? 'wird ins Modell zurückgeschrieben'
      : 'wird nicht zurückgeschrieben',
  ].filter(Boolean).join(' · ');

  /* Ein Verweis ist kein Text zum Tippen. Er entsteht beim PDF-Upload und
     wird von Hand nicht geändert — also anklickbar zeigen, nicht in ein
     Eingabefeld sperren. */
  if (f.art === 'url') {
    return (
      <div>
        <div style={{ fontSize: 10, color: 'var(--schwach, #666)' }}>
          {f.bezeichnung}
        </div>
        {wert ? (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <a href={wert} target="_blank" rel="noreferrer"
               style={{ fontSize: 11, flex: 1, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
               title={wert}>
              öffnen
            </a>

            <button
              className="m-knopf"
              onClick={async (e) => {
                e.preventDefault();

                const geklappt = await inDieAblage(wert);

                if (!geklappt) {
                  alert('Der Browser erlaubt das Kopieren nicht. Der Verweis:'
                      + '\n\n' + wert);
                }
              }}
              style={{ fontSize: 9, padding: '0 4px' }}
              title="Der Verweis ist ohne Anmeldung erreichbar"
            >
              kopieren
            </button>

            <button className="m-knopf"
                    onClick={(e) => { e.preventDefault(); setzen(''); }}
                    style={{ fontSize: 9, padding: '0 4px' }}
                    aria-label="Verweis entfernen"
                    title="Verweis entfernen">
              ×
            </button>
          </div>
        ) : (
          <div className="sehr-schwach" style={{ fontSize: 11 }}>
            — entsteht beim PDF-Upload
          </div>
        )}
      </div>
    );
  }

  if (f.art === 'ja_nein') {
    return (
      <MusterFeld
        art="auswahl"
        beschriftung={f.bezeichnung}
        wert={wert}
        beiAenderung={(w) => setzen(w as string)}
        hinweis={hinweis}
        optionen={[
          { wert: '', beschriftung: '—' },
          { wert: 'Ja', beschriftung: 'Ja' },
          { wert: 'Nein', beschriftung: 'Nein' },
        ]}
        breite="drittel"
      />
    );
  }

  if (auswahl.length > 0) {
    return (
      <MusterFeld
        art="auswahl"
        beschriftung={f.bezeichnung}
        wert={wert}
        beiAenderung={(w) => setzen(w as string)}
        hinweis={hinweis}
        optionen={[
          { wert: '', beschriftung: '—' },
          ...auswahl.map((a) => ({ wert: a, beschriftung: a })),
          // Ein vorhandener Wert, der nicht in der Auswahl steht, darf nicht
          // verschwinden — sonst löscht ein Öffnen der Zeile den Wert.
          ...(wert && !auswahl.includes(wert)
            ? [{ wert, beschriftung: `${wert} (nicht in der Auswahl)` }] : []),
        ]}
        breite="drittel"
      />
    );
  }

  if (f.art === 'zahl') {
    return (
      <MusterFeld
        art="zahl"
        beschriftung={f.bezeichnung}
        einheit={f.einheit ?? undefined}
        wert={wert === '' ? null : leseZahl(wert)}
        beiAenderung={(w) => setzen(w === null ? '' : String(w))}
        hinweis={hinweis}
        breite="drittel"
      />
    );
  }

  return (
    <MusterFeld
      beschriftung={f.bezeichnung}
      wert={wert}
      beiAenderung={(w) => setzen(w as string)}
      hinweis={hinweis}
      breite="drittel"
    />
  );
}

// === Die Vorlage im Kopf =====================================================

/**
 * Die Importvorlage herunterladen.
 *
 * Nur das — das Einlesen geschieht in der Zeile des Fabrikats.
 */
function Vorlagenknopf({
  api, projektId, art, melde, neuLaden,
}: {
  api: ApiClient;
  projektId: string;
  art: string;
  melde: (m: string) => void;
  neuLaden: () => void;
}) {
  const [laeuft, setLaeuft] = useState(false);
  const [probe, setProbe] = useState<string[] | null>(null);

  const vorlage = async () => {
    try {
      const v = await api.elektroDatenVorlage(projektId, art || undefined);

      const blob = new Blob([JSON.stringify(v, null, 2)],
        { type: 'application/json' });

      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `elektro-vorlage${art ? '-' + art : ''}.json`;
      a.click();

      URL.revokeObjectURL(a.href);
    } catch (e) {
      melde(e instanceof Error ? e.message : String(e));
    }
  };

  /**
   * Alle Fabrikate einer Datei einlesen — ohne Zeilenbezug.
   *
   * Ohne `familie` erkennt der Endpunkt die Vorlagengestalt und liest jedes
   * Fabrikat, das im Projekt vorkommt. In der Zeile wird nur das gewählte gelesen —
   * das ist dort richtig, aber wer eine Datei mit zwanzig Fabrikaten hat, will sie
   * nicht zwanzigmal anklicken.
   */
  const alleEinlesen = async (datei: File, nurPruefen: boolean) => {
    setLaeuft(true);
    setProbe(null);

    try {
      const a = await api.elektroDatenImport(projektId, datei, nurPruefen);

      if (!a) { melde('Keine Antwort von der Schnittstelle.'); return; }

      if (nurPruefen) {
        const zeilen = [a.message];

        if (a.unbekannteFabrikate.length > 0)
          zeilen.push('Nicht im Projekt: ' + a.unbekannteFabrikate.join(', '));

        if (a.unbekannteFelder.length > 0)
          zeilen.push('Unbekannte Felder: ' + a.unbekannteFelder.join(', '));

        if (a.abgewiesen.length > 0)
          zeilen.push('Abgewiesen: ' + a.abgewiesen.join('; '));

        setProbe(zeilen);
      } else {
        melde(a.message);
        neuLaden();
      }
    } catch (e) {
      melde(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div style={{ borderTop: '1px solid var(--linie, #eee)', paddingTop: 10 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                    flexWrap: 'wrap' }}>
        <button onClick={vorlage} style={{ fontSize: 12 }}
                title="Die Fabrikate des Projekts mit leeren Feldern">
          Vorlage herunterladen
        </button>

        <label className="knopf" style={{ fontSize: 12 }}>
          prüfen
          <input type="file" accept="application/json,.json" hidden
                 disabled={laeuft}
                 onChange={(e) => {
                   const d = e.target.files?.[0];
                   if (d) alleEinlesen(d, true);
                   e.target.value = '';
                 }} />
        </label>

        <label className="knopf betont" style={{ fontSize: 12 }}>
          {laeuft ? 'wird gelesen …' : 'alle Fabrikate einlesen'}
          <input type="file" accept="application/json,.json" hidden
                 disabled={laeuft}
                 onChange={(e) => {
                   const d = e.target.files?.[0];
                   if (d) alleEinlesen(d, false);
                   e.target.value = '';
                 }} />
        </label>

        <span className="sehr-schwach" style={{ fontSize: 11, flexBasis: '100%' }}>
          Hier werden <strong>alle</strong> Fabrikate der Datei eingelesen. In der
          Liste darunter jeweils nur das gewählte — dort ist es meist gemeint.
          Beide Wege lesen dieselbe Vorlagengestalt.
        </span>
      </div>

      {probe && (
        <div className="hinweis" style={{ display: 'block', marginTop: 10 }}>
          <IconWarnung size={13} />

          <div style={{ fontSize: 12, whiteSpace: 'pre-line', flex: 1 }}>
            {probe.join('\n')}
          </div>

          <button onClick={() => setProbe(null)}
                  style={{ marginTop: 6, padding: '1px 7px', fontSize: 11 }}>
            schließen
          </button>
        </div>
      )}
    </div>
  );
}
